import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Fuel, Pencil, Plus, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";

import { formatNumber, validateCultivationNumber } from "../../../../utils/utils";
import { CULTIVATION_TYPE, ENERGY_UNIT_TYPE } from "../../../../config/constants";
import { useGetEnergySource } from "../../../../api/static-data";

import "../styles/CultivarForm.scss";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import CultivarSaveReminder from "./CultivarSaveReminder";
import { useCultivarSectionAccordion } from "../hooks/useCultivarSectionAccordion";

const FuelUse = forwardRef((
  {
    currentFormData,
    currentDataType,
    lockStatus = false,
    disabled = false,
    sectionsDisabled = false
  }: any,
  ref
) => {
  const { accordionEnabled, toggleAccordion, headerClassName } = useCultivarSectionAccordion(
    sectionsDisabled
  );
  const [isEditingEntry, setIsEditingEntry] = useState(false);
  const [formEntryKey, setFormEntryKey] = useState(0);
  const [fuelUseData, setFuelUseData] = useState<any[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false)
  const [deleteValue, setDeleteValue] = useState<any>({
    item: "",
    type: "",
  });

  const currentFuelUse = useMemo(() => {
    return currentFormData?.cultivationField?.fuelUse || currentFormData?.fuelUse || []
  }, [currentFormData])  

  const fuelUseDetails = useCallback(() => {
    const currentFualUseData = currentFuelUse?.map(
      (item: any) => {
        return {
          ...item,
          id: parseInt(item.id),
        };
      }
    );
    setFuelUseData(currentFualUseData ? currentFualUseData : []);
  }, [currentFormData,currentFuelUse])  

  useEffect(() => {
    fuelUseDetails()
  }, [currentFormData])

  const {
    data: energySourceData,
  } = useGetEnergySource();

  const emptyFormValues = useMemo(
    () => ({
      energyUsed: "",
      energySource: "",
      energyUnitType: ""
    }),
    []
  );

  const form = useForm({
    defaultValues: emptyFormValues,
    mode: "all",
    reValidateMode: "onChange"
  });
  const { control, handleSubmit, reset, clearErrors } = form;

  const resetFuelUseForm = useCallback(() => {
    clearErrors();
    reset(emptyFormValues);
    setIsEditingEntry(false);
    setEditIndex(null);
    setFormEntryKey(key => key + 1);
  }, [clearErrors, emptyFormValues, reset]);

  useImperativeHandle(ref, () => ({
    getFuelUse: () => {
      return fuelUseData
    }
  }));

  const onEditFuelUse = (item: any, index: number) => {
    setIsEditingEntry(true);
    setEditIndex(index);
    reset({
      energyUsed: item?.energyUsed?.toString() ?? "",
      energySource: item.energySource.toString(),
      energyUnitType: item.energyUnit.toString()
    });
    setFormEntryKey(key => key + 1);
  };

  const addFuelUse = handleSubmit(formValues => {
    const fuelUsedEntry = {
      energySource: Number(formValues.energySource),
      energyUsed: Number(formValues.energyUsed),
      energyUnit: Number(formValues.energyUnitType)
    };

    if (isEditingEntry && editIndex !== null) {
      const updatedData = [...fuelUseData];
      updatedData[editIndex] = { ...updatedData[editIndex], ...fuelUsedEntry };
      setFuelUseData(updatedData);
    } else {
      setFuelUseData(prev => [...prev, fuelUsedEntry]);
    }
    resetFuelUseForm();
  });

   const deleteFuelUse = useCallback(() => {
      setFuelUseData((prevList: any[]) =>
        prevList.filter((_, index: number) => index !== deleteValue.index)
      );
      setVisibleDeleteModal(false);
      setDeleteValue({});
    }, [deleteValue]);
    
    const deleteFuelUseData = (item: any, index: number) => {
      setVisibleDeleteModal(true);
      setDeleteValue({
        type: "FuelUsed",
        item: item ?? "",
        index: index,
      });
    };

  const handleClick = toggleAccordion;

  return currentDataType === CULTIVATION_TYPE.Supplier ? (
    <Card className="tw-border-border tw-p-0 tw-gap-0">
      <CardHeader className={headerClassName} onClick={handleClick}>
        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
          <div className="tw-flex tw-items-center tw-gap-2"><Fuel className="tw-h-4 tw-w-4 tw-text-primary" /><span className="tw-text-sm tw-font-semibold tw-text-foreground">Fuel Use</span></div>
          {accordionEnabled ? <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" /> : <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />}
        </div>
      </CardHeader>
      <CardContent
        className={`tw-py-3.5 tw-px-3.5 tw-space-y-4 ${accordionEnabled ? "" : "tw-hidden"}`}
      >
          <Form {...form}>
          <div className="tw-space-y-4">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-[1fr_1fr_1fr_auto] tw-items-start tw-gap-4">
              <FormSelect
                control={control}
                name="energySource"
                label="Energy Source"
                placeholder="Select Energy Source"
                options={energySourceData ?? []}
                resetKey={formEntryKey}
                required
                errorMessage="Energy source is required"
                disabled={disabled}
              />
              <FormInputNumber
                control={control}
                label="Energy Used"
                placeholder="Energy Used"
                name="energyUsed"
                required
                errorMessage="Energy used required"
                disabled={disabled}
                rules={{
                  validate: {
                    valueCheck: (v: any) => validateCultivationNumber(v)
                  }
                }}
              />
              <FormSelect
                control={control}
                name="energyUnitType"
                label="Energy Unit type"
                placeholder="Select Energy Unit type"
                options={ENERGY_UNIT_TYPE}
                resetKey={formEntryKey}
                required
                errorMessage="Energy unit type is required"
                disabled={disabled}
              />
              <div className="tw-space-y-2 tw-mt-2">
                <div
                  className="tw-text-sm tw-font-medium tw-leading-none tw-text-foreground tw-opacity-0 tw-select-none"
                  aria-hidden
                >
                  Add
                </div>
                <Button
                  type="button"
                  size="lg"
                  className={`!tw-h-10 ${isEditingEntry ? "" : "!tw-w-10 !tw-px-0"}`}
                  disabled={lockStatus || disabled}
                  onClick={addFuelUse}
                >
                  {isEditingEntry ? "Update" : <Plus className="tw-h-4 tw-w-4" />}
                </Button>
              </div>
          </div>
          {fuelUseData?.length > 0 && (
            <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="tw-border-b tw-border-border hover:tw-bg-transparent">
                    <TableHead
                      className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                      style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                    >
                      Energy Source
                    </TableHead>
                    <TableHead
                      className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                      style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                    >
                      Energy Used
                    </TableHead>
                    <TableHead
                      className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                      style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                    >
                      Energy Unit type
                    </TableHead>
                    {!lockStatus && (
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground tw-text-right tw-w-24"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fuelUseData?.map((item: any, index: any) => (
                    <TableRow key={`fuelUse_${item?.id ?? index}`} className="hover:tw-bg-muted/20">
                      <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                        {energySourceData?.find((e: any) => e?.value == item?.energySource)?.text ??
                          ""}
                      </TableCell>
                      <TableCell className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-muted-foreground">
                        {formatNumber(item?.energyUsed) ?? ""}
                      </TableCell>
                      <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                        {ENERGY_UNIT_TYPE?.find((e: any) => e?.key == item?.energyUnit)?.text ?? ""}
                      </TableCell>
                      {!lockStatus && (
                        <TableCell className="tw-px-4 tw-py-3">
                          <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="tw-h-7 tw-w-7 tw-p-0"
                              onClick={() => {
                                onEditFuelUse(item, index);
                              }}
                            >
                              <Pencil className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                              onClick={() => deleteFuelUseData(item, index)}
                            >
                              <Trash2 className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          </div>
          </Form>
          <CultivarSaveReminder section="fuelUse" hidden={lockStatus || disabled} />
        </CardContent>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteValue({});
        }}
        approve={deleteFuelUse}
        title={`Delete ${deleteValue.type}`}
        subTitle={`Are you sure you want to delete Fuel use data?`}
      />
    </Card>
  ) : null;
})

export default FuelUse;
